import type { Axis, Tick } from 'highcharts';
import { isEqual } from 'lodash';
import moment from 'moment';
import type { RefObject } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { RoadmapEvent } from '../../interfaces/event';
import type { IMap } from '../../interfaces/generic';
import type { IEventCoordinates } from '../../interfaces/projection';

interface IProps {
    xAxisYPosition: RefObject<number | undefined>;
    allEvents: RoadmapEvent[];
    projectionPeriodYears: number;
}

type IEventPaneItem = RoadmapEvent & { year: number };

export const useYearEventMap = ({ xAxisYPosition, projectionPeriodYears, allEvents }: IProps) => {
    const [yearEventMap, setYearEventMap] = useState<IMap<IEventCoordinates> | undefined>(undefined);
    const enrichedLiveEvents = useMemo(() => {
        const cashflowRelevantFilter = (event: RoadmapEvent) => moment(event.dueDate).diff(moment(), 'years') < projectionPeriodYears;

        return (allEvents || []).filter(cashflowRelevantFilter).map((event) => ({
            ...event,
            year: moment(event.dueDate).year(),
        })) as IEventPaneItem[];
    }, [allEvents, projectionPeriodYears]);

    const getYear = useCallback((unixEpoch: number) => moment(unixEpoch).year(), []);

    const getYearCashEventMap = useCallback(
        (ticksArray: any[], xAxis: Axis) => {
            const yearCashEventMap: IMap<IEventCoordinates> = {};
            const sortedTicks = ticksArray
                .filter((tick: Tick) => Object.keys(tick).length > 0 && Object.prototype.hasOwnProperty.call(tick, 'pos'))
                .sort((tick, nextTick) => tick.pos - nextTick.pos);
            const firstTickPosition = xAxis.toPixels(sortedTicks[0]?.pos, false);
            const lastTickTickPosition = xAxis.toPixels(sortedTicks[sortedTicks.length - 1]?.pos, false);
            const totalDistance = lastTickTickPosition - firstTickPosition;
            const firstYear = getYear(sortedTicks[0]?.pos);
            const lastYear = getYear(sortedTicks[sortedTicks.length - 1]?.pos);
            const numberOfYears = lastYear - firstYear;
            const tickInterval = totalDistance / numberOfYears;
            let xPosition = firstTickPosition;
            let yearCounter = firstYear;
            for (let i = 0; i <= numberOfYears; i++) {
                yearCashEventMap[yearCounter] = {
                    x: xPosition,
                    y: xAxisYPosition.current!,
                };

                xPosition += tickInterval;
                yearCounter++;
            }

            return yearCashEventMap;
        },
        [getYear, xAxisYPosition],
    );

    const getEventsMap = useCallback(
        (yearEventMap?: IMap<IEventCoordinates>) => {
            const yearCashEventMap: IMap<IEventCoordinates> = yearEventMap ?? {};
            enrichedLiveEvents.forEach((event) => {
                const eventsAtCoordinate = yearCashEventMap[event.year]?.eventsAtCoordinate || [];
                eventsAtCoordinate.push(event);
                yearCashEventMap[event.year] = {
                    ...yearCashEventMap[event.year],
                    showSingleIcon: eventsAtCoordinate.length <= 1,
                    eventsAtCoordinate,
                };
            });

            return yearCashEventMap;
        },
        [enrichedLiveEvents],
    );

    const updateEventCoordinates = useCallback(
        (chart: Highcharts.Chart) => {
            const chartData = chart.xAxis[0];
            const yearCashEventMap = getYearCashEventMap(Object.values(chartData.ticks), chartData.chart.xAxis[0]);
            const eventsMap = getEventsMap(yearCashEventMap);
            if (!isEqual(yearEventMap, eventsMap)) setYearEventMap(eventsMap);
        },
        [yearEventMap, getEventsMap, getYearCashEventMap],
    );

    return { yearEventMap, getEventsMap, updateEventCoordinates };
};
